// Sugalarm — Cycle 456: the six preset hero pictures.
//
// A CLOSED set, defined once here and once on the server
// (`server/models/emergency_contact.py::HERO_AVATAR_PRESETS`);
// `server/tests/test_cycle456_hero_attribution.py` reads the list below and
// fails the build if the two disagree. List order is the server's
// assignment order (a new hero takes the first unused key).
//
// The art is the operator's superhero set; the key → picture mapping and
// the alt text were adjusted to it by operator decision (2026-09-30), so
// the alt text describes what is actually drawn. The serving copies are
// WebP at 256 and 128 px under `/avatars/heroes/` (built by
// `scripts/build_hero_avatars.py`); every WebView this app runs in decodes
// WebP, so there is no PNG fallback. A missing or unknown key falls back to
// an initials monogram — never a throw, never a broken image.

export const HERO_AVATAR_PRESETS = [
  "kitty-1",
  "kitty-2",
  "kitty-3",
  "puppy-1",
  "puppy-2",
  "puppy-3",
] as const;

export type HeroAvatarPreset = (typeof HERO_AVATAR_PRESETS)[number];

/** Alt text per preset — what each picture actually shows (cycle 457,
 *  written by looking at the art). The SAME strings label the chooser's
 *  preset grid and the "Who's treating?" tiles. */
export const HERO_AVATAR_ALT: Record<HeroAvatarPreset, string> = {
  "kitty-1": "Cream tabby kitten in a purple cape and gold armor",
  "kitty-2": "Gray tabby kitten in a blue cape",
  "kitty-3": "Calico kitten in a pink mask and cape, leaping",
  "puppy-1": "Golden puppy with a blue mask and red cape",
  "puppy-2": "Black and white puppy with a blue mask and cape",
  "puppy-3": "Tan and white puppy with a blue mask and scarf",
};

/** The soft disc each picture sits on (the mockup's tile circles). */
export const HERO_AVATAR_TINT: Record<HeroAvatarPreset, string> = {
  "kitty-1": "#f3e6ff",
  "kitty-2": "#dfe8ff",
  "kitty-3": "#ffe0ea",
  "puppy-1": "#ffeccc",
  "puppy-2": "#dfe8ff",
  "puppy-3": "#dff3ff",
};

export function isHeroAvatarPreset(key: unknown): key is HeroAvatarPreset {
  return (
    typeof key === "string" &&
    (HERO_AVATAR_PRESETS as readonly string[]).includes(key)
  );
}

/** The served picture for a preset, at the smallest derivative that is at
 *  least `px` on the device (2× for density). Null for an unknown key. */
export function heroAvatarSrc(key: unknown, px: number): string | null {
  if (!isHeroAvatarPreset(key)) return null;
  const size = px * 2 > 128 ? 256 : 128;
  return `/avatars/heroes/${key}-${size}.webp`;
}

/** One or two initials for the monogram fallback. Never throws; an empty
 *  or symbol-only name yields "?". */
export function heroMonogram(name: string | null | undefined): string {
  const words = (name ?? "")
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ""))
    .filter(Boolean);
  if (words.length === 0) return "?";
  const firstWord = words[0] ?? "";
  const lastWord = words[words.length - 1] ?? "";
  const first = Array.from(firstWord)[0] ?? "";
  const second = words.length > 1 ? Array.from(lastWord)[0] ?? "" : "";
  return (first + second).toUpperCase() || "?";
}
